import { Navigate, Outlet, useLocation } from 'react-router';
import { useContext, useEffect, useRef } from 'react';
import { AuthContext } from '../../context/AuthContext';
import Sidebar from '../../components/Sidebar';
import Header from '../../components/Header';
import './MainLayout.scss';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { UserRoleEnum } from '../../types/apiGeneratedTypes';
import { getBodySystemsList } from '../../api/body-systems';
import {
  setBiomarkersCount,
  setBiomarkersList,
  setBodySystemsList,
  setBsCount,
  setNotesCount
} from '../../store/appSlice';
import { getBiomarkersList } from '../../api/biomarkers';
import { setAlertsCount } from '../../store/alertsSlice';
import { getUnprocessedTestResultsCount } from '../../api/testResults';
import { getNotesCount } from '../../api/counters';
import { setTotalUnreadCount } from '../../store/chatSlice';
import { getTotalUnreadCount } from '../../api/chat';

const isSuperAdminUser = (admin: RootState['admin']['admin']) =>
  admin.role === UserRoleEnum.SuperAdmin && admin.is_super_admin === true;

const MainLayout = () => {
  const { auth, isLoading } = useContext(AuthContext);
  const location = useLocation();
  const dispatch = useDispatch();
  const admin = useSelector((state: RootState) => state.admin.admin);
  const currentPage = useSelector((state: RootState) => state.page);
  const { bodySystemsList, bsCount, biomarkersCount, biomarkersList, notesCount } =
    useSelector((state: RootState) => state.app);
  const { alertsCount } = useSelector((state: RootState) => state.alerts);
  const { unreadCount } = useSelector((state: RootState) => state.chat);
  const bodySystemsListLengthRef = useRef(bodySystemsList.length);
  const biomarkersListLengthRef = useRef(biomarkersList.length);
  const bsCountRef = useRef(bsCount);
  const biomarkersCountRef = useRef(biomarkersCount);
  const alertsCountRef = useRef(alertsCount);
  const notesCountRef = useRef(notesCount);
  const unreadCountRef = useRef(unreadCount);
  bodySystemsListLengthRef.current = bodySystemsList.length;
  biomarkersListLengthRef.current = biomarkersList.length;
  bsCountRef.current = bsCount;
  biomarkersCountRef.current = biomarkersCount;
  alertsCountRef.current = alertsCount;
  notesCountRef.current = notesCount;
  unreadCountRef.current = unreadCount;
  const sidebarWidth = currentPage.sidebarCollapsed ? '0' : '292px';
  const style = {
    paddingLeft: currentPage.showSidebar ? sidebarWidth : '0px',
    transition: 'padding-left 0.25s ease'
  };

  useEffect(() => {
    const fetchBodySystems = async () => {
      try {
        const bodySystemsList = await getBodySystemsList();
        dispatch(setBodySystemsList(bodySystemsList.body_systems));
        if (!bsCountRef.current) {
          dispatch(setBsCount(bodySystemsList.body_systems.length));
        }
      } catch (error) {
        console.log('Error loading getBodySystemsList', error);
      }
    };
    const fetchBiomarkers = async () => {
      try {
        const biomarkersList = await getBiomarkersList();
        dispatch(setBiomarkersList(biomarkersList.biomarkers));
        if (!biomarkersCountRef.current) {
          dispatch(setBiomarkersCount(biomarkersList.biomarkers.length));
        }
      } catch (error) {
        console.log('Error loading getBodySystemsList', error);
      }
    };
    const fetchAlertsCount = async () => {
      try {
        const alerts = await getUnprocessedTestResultsCount();
        dispatch(setAlertsCount(alerts.count));
      } catch (error) {
        console.log('Error loading getUnprocessedTestResultsCount', error);
      }
    };

    const fetchNotesCount = async () => {
      try {
        const notes = await getNotesCount();
        dispatch(setNotesCount(notes.total_count));
      } catch (error) {
        console.log('Error loading getNotesCount', error);
      }
    };

    const fetchChatUnreadCount = async () => {
      try {
        const totalCount = await getTotalUnreadCount();
        dispatch(setTotalUnreadCount(totalCount));
      } catch (error) {
        console.log('Error loading getTotalUnreadCount', error);
      }
    };

    if (bodySystemsListLengthRef.current === 0 && auth.isAuthenticated) {
      fetchBodySystems();
    }
    if (!alertsCountRef.current && auth.isAuthenticated) {
      fetchAlertsCount();
    }
    if (!notesCountRef.current && auth.isAuthenticated) {
      fetchNotesCount();
    }
    if (!unreadCountRef.current && auth.isAuthenticated) {
      fetchChatUnreadCount();
    }
    if (biomarkersListLengthRef.current === 0 && auth.isAuthenticated) {
      fetchBiomarkers();
    }
  }, [auth.isAuthenticated, dispatch]);

  useEffect(() => {
    if (!auth.isAuthenticated) {
      return;
    }

    const refreshAlertsCount = async () => {
      try {
        const alerts = await getUnprocessedTestResultsCount();
        dispatch(setAlertsCount(alerts.count));
      } catch (error) {
        console.log('Error refreshing alerts count', error);
      }
    };

    const refreshChatUnreadCount = async () => {
      try {
        const totalCount = await getTotalUnreadCount();
        dispatch(setTotalUnreadCount(totalCount));
      } catch (error) {
        console.log('Error refreshing chat unread count', error);
      }
    };

    const doRefresh = async () => {
      try {
        await Promise.all([refreshAlertsCount(), refreshChatUnreadCount()]);
      } catch (e) {
      }
    };

    doRefresh();
    
    const intervalId = setInterval(doRefresh, 30000);
    
    return () => clearInterval(intervalId);
  }, [auth.isAuthenticated, dispatch]);

  // Refresh counts when location changes (same pattern as alerts)
  useEffect(() => {
    if (!auth.isAuthenticated) {
      return;
    }

    const refreshAlertsCount = async () => {
      try {
        const alerts = await getUnprocessedTestResultsCount();
        dispatch(setAlertsCount(alerts.count));
      } catch (error) {
        console.log('Error refreshing alerts count on navigation', error);
      }
    };

    const refreshChatUnreadCount = async () => {
      try {
        const totalCount = await getTotalUnreadCount();
        dispatch(setTotalUnreadCount(totalCount));
      } catch (error) {
        console.log('Error refreshing chat unread count on navigation', error);
      }
    };

    // Refresh both counts when navigating between pages
    Promise.all([refreshAlertsCount(), refreshChatUnreadCount()]).catch(() => {
      // Silently handle errors
    });
  }, [location.pathname, auth.isAuthenticated, dispatch]);

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (!auth.isAuthenticated) {
    window.location.href = 'https://corevitals.me';
    return null;
  }

 
  if (!isSuperAdminUser(admin)) {
    const p = location.pathname;
    if (
      p === '/' ||
      p === '/scheduler' ||
      p === '/audit-logs' ||
      p === '/body-systems' ||
      p === '/marketing-cms' ||
      p === '/doctors' ||
      p === '/nutritionists' ||
      p === '/clinics' ||
      p === '/teams'
    ) {
      return <Navigate to='/members' replace />;
    }
  }

  return (
    <>
      <div className={`container ${currentPage.sidebarCollapsed ? ' collapsed' : ''}`} style={style}>
        <Sidebar />
        <Header />
        <Outlet />
      </div>
    </>
  );
};

export default MainLayout;
